import { useRef } from 'react';
import { SECTION_IDS, site } from '../content/site';
import { gsap, useGSAP, EASE, MOTION_OK, REDUCED } from '../lib/gsap';
import { WorkGroup } from '../components/work/WorkGroup';

const TITLE_ID = 'work-title';

/**
 * Réalisations: a header, then three groups. Each group has a full-width heading that
 * never changes while it is read, and its projects below it, every one title first and
 * its media after. No sticky column, nothing that swaps as you scroll.
 *
 * Motion (sober, and only where it helps reading): the section header and each group
 * heading rise once as they come into view, and each article arrives once (fade and
 * translate, never a scale). Captures float under a mouse (TiltCard, fine pointers only).
 * The Vox command and recordings and the Brn panel run their own, motivated, motion.
 * Reduced motion: no reveal, no tilt, everything is in place as rendered.
 */
export function Work() {
	const root = useRef<HTMLElement>(null);
	const { eyebrow, title, intro, groups } = site.work;

	useGSAP(
		() => {
			const mm = gsap.matchMedia();
			mm.add({ motion: MOTION_OK, reduced: REDUCED }, (context) => {
				if (context.conditions?.reduced) return;

				// The chapter opens: eyebrow, title, intro in reading order.
				const head = gsap.utils.toArray<HTMLElement>('[data-work-head]');
				if (head.length) {
					gsap.from(head, {
						y: 28,
						opacity: 0,
						duration: 1.1,
						stagger: 0.08,
						scrollTrigger: { trigger: head[0], start: 'top 85%', once: true },
					});
				}
				// Each group heading (and its closing note) arrives once.
				gsap.utils.toArray<HTMLElement>('[data-work-group-head]').forEach((el) => {
					gsap.from(el, {
						y: 28,
						opacity: 0,
						duration: 1,
						scrollTrigger: { trigger: el, start: 'top 88%', once: true },
					});
				});
				// Each project arrives once, as the reader reaches it.
				gsap.utils.toArray<HTMLElement>('[data-work-article]').forEach((article) => {
					gsap.from(article, {
						y: 40,
						opacity: 0,
						duration: 1,
						ease: EASE,
						scrollTrigger: { trigger: article, start: 'top 88%', once: true },
					});
				});
			});
		},
		{ scope: root },
	);

	return (
		<section id={SECTION_IDS.work} ref={root} aria-labelledby={TITLE_ID} className="section-pad">
			<div className="container-site">
				<div className="grid gap-y-6 lg:grid-cols-12 lg:items-end lg:gap-x-10">
					<div className="lg:col-span-7">
						<p
							data-work-head
							className="text-[13px] font-medium uppercase tracking-[0.16em] text-graphite-muted dark:text-dusk-muted"
						>
							{eyebrow}
						</p>
						<h2
							id={TITLE_ID}
							data-work-head
							className="mt-6 font-display text-[clamp(2.375rem,6.4vw,5rem)] font-bold leading-[1] tracking-[-0.04em] text-graphite dark:text-mineral"
						>
							{title}
						</h2>
					</div>
					<p
						data-work-head
						className="max-w-prose text-[1.0625rem] leading-relaxed text-graphite-muted dark:text-dusk-muted lg:col-span-5 lg:pb-2"
					>
						{intro}
					</p>
				</div>

				<div className="mt-[clamp(4rem,9vw,8rem)] grid gap-y-[clamp(6rem,12vw,11rem)]">
					{groups.map((group) => (
						<WorkGroup key={group.id} group={group} />
					))}
				</div>
			</div>
		</section>
	);
}
